import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { TreeView } from "@mui/x-tree-view";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ReplayIcon from "@mui/icons-material/Replay";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { getRequestWithToken } from "../../../utils/ApiRequests";
import ObjectIdBadge from "./ObjectIdBadge";
import useHierarchyRoleSearch, { canSearchTreeRoles } from "./useHierarchyRoleSearch";

export const getOccupationNodeId = (sectorId, occupationId) => `occupation:${sectorId}:${occupationId}`;
export const getRoleNodeId = (sectorId, occupationId, roleId) => `role:${sectorId}:${occupationId}:${roleId}`;
export const getSelectedNodeId = (sectorId, occupationId, roleId) => {
  if (roleId && occupationId && sectorId) return getRoleNodeId(sectorId, occupationId, roleId);
  if (occupationId && sectorId) return getOccupationNodeId(sectorId, occupationId);
  if (sectorId) return `sector:${sectorId}`;
  return "";
};

export const groupHierarchySearchResults = (results = []) => {
  const sectorMap = new Map();
  results.forEach((result) => {
    if (!result?.sector?._id || !result?.occupation?._id || !result?.role?._id) return;
    if (!sectorMap.has(result.sector._id)) {
      sectorMap.set(result.sector._id, {
        _id: result.sector._id,
        sectorNameEn: result.sector.name,
        occupations: new Map(),
      });
    }
    const sector = sectorMap.get(result.sector._id);
    if (!sector.occupations.has(result.occupation._id)) {
      sector.occupations.set(result.occupation._id, {
        _id: result.occupation._id,
        occupationNameEn: result.occupation.name,
        roles: new Map(),
      });
    }
    sector.occupations.get(result.occupation._id).roles.set(result.role._id, {
      _id: result.role._id,
      jobRoleNameEn: result.role.name,
      ncoCode: result.role.ncoCode || "",
    });
  });

  return [...sectorMap.values()].map((sector) => ({
    ...sector,
    occupations: [...sector.occupations.values()].map((occupation) => ({
      ...occupation,
      roles: [...occupation.roles.values()],
    })),
  }));
};

export const groupCandidateMappings = (mappings = []) => {
  const familyMap = new Map();
  mappings.forEach((mapping) => {
    const familyKey = mapping.family?.family_id || mapping.family?.family_name || `family-${mapping._id}`;
    const groupKey = mapping.role_group?.role_group_id || mapping.role_group?.role_group_name || `group-${mapping._id}`;
    if (!familyMap.has(familyKey)) {
      familyMap.set(familyKey, {
        key: familyKey,
        name: mapping.family?.family_name || mapping.family?.family_id || "Unnamed family",
        icon: mapping.family?.family_icon || "",
        objectId: mapping.family?._id || "",
        businessId: mapping.family?.family_id || "",
        groups: new Map(),
      });
    }
    const family = familyMap.get(familyKey);
    if (!family.groups.has(groupKey)) {
      family.groups.set(groupKey, {
        key: groupKey,
        name: mapping.role_group?.role_group_name || mapping.role_group?.role_group_id || "Unnamed group",
        objectId: mapping.role_group?._id || "",
        businessId: mapping.role_group?.role_group_id || "",
        mappings: [],
      });
    }
    family.groups.get(groupKey).mappings.push(mapping);
  });

  return [...familyMap.values()].map((family) => ({
    ...family,
    groups: [...family.groups.values()],
  }));
};

export const normalizeCanonicalLink = (link = {}) => ({
  _id: link._id,
  isCanonicalLink: true,
  family: link.candidateRole?.family ? {
    _id: link.candidateRole.family._id,
    family_id: link.candidateRole.family.familyId,
    family_name: link.candidateRole.family.name,
    family_icon: link.candidateRole.family.icon || "",
  } : {},
  role_group: link.candidateRole?.group ? {
    _id: link.candidateRole.group._id,
    role_group_id: link.candidateRole.group.groupId,
    role_group_name: link.candidateRole.group.name,
  } : {},
  role: link.candidateRole ? {
    _id: link.candidateRole._id,
    role_id: link.candidateRole.roleId,
    role_name: link.candidateRole.name,
    aliases: { en: link.candidateRole.aliases || [] },
    allowed_level_ids: link.candidateRole.allowedLevelIds || [],
  } : {},
});

export const buildCanonicalNcoLinkUrl = (roleId) => {
  const params = new URLSearchParams({ mapped: "true", nco_job_role_id: roleId, limit: "1", page: "1" });
  return `../v3/admin/candidate-role-mappings/nco-links?${params.toString()}`;
};

const levelStyles = {
  sector: { color: "#5B5BD6", background: "rgba(91, 91, 214, 0.14)", selectedLight: "rgba(91, 91, 214, 0.13)", selectedDark: "rgba(125, 125, 235, 0.17)", Icon: CategoryOutlinedIcon, tileSize: 34, iconSize: 20, fontSize: 14, fontWeight: 650 },
  occupation: { color: "#E86F00", background: "rgba(232, 111, 0, 0.14)", selectedLight: "rgba(255, 140, 0, 0.18)", selectedDark: "rgba(255, 140, 0, 0.18)", Icon: AccountTreeOutlinedIcon, tileSize: 30, iconSize: 18, fontSize: 13.5, fontWeight: 550 },
  role: { color: "#25766C", background: "rgba(37, 118, 108, 0.14)", selectedLight: "rgba(37, 118, 108, 0.14)", selectedDark: "rgba(68, 160, 148, 0.18)", Icon: WorkOutlineIcon, tileSize: 27, iconSize: 16, fontSize: 13, fontWeight: 450 },
};

const HierarchyNodeLabel = ({ level, name, objectId, ncoCode = "", mappingCount, countLabel = "mapping", onCopy, onActivate }) => {
  const style = levelStyles[level];
  const Icon = style.Icon;
  return (
    <Box display="flex" alignItems="center" gap={1.1} py={0.45} minWidth={0} width="100%">
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: style.tileSize, height: style.tileSize, flexShrink: 0, color: style.color, bgcolor: style.background, borderRadius: 1.6, border: `1px solid ${style.background}` }}>
        <Icon sx={{ fontSize: style.iconSize }} />
      </Box>
      <Box
        minWidth={0}
        flex={1}
        onClick={onActivate ? (event) => { event.preventDefault(); event.stopPropagation(); onActivate(); } : undefined}
        sx={{ cursor: onActivate ? "pointer" : "default" }}
      >
        <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
          <Typography sx={{ fontSize: style.fontSize, fontWeight: style.fontWeight, lineHeight: 1.25 }} noWrap>{name}</Typography>
          {ncoCode && <Chip label={`NCO ${ncoCode}`} size="small" sx={{ height: 18, fontSize: 9, color: style.color, bgcolor: style.background }} />}
          {mappingCount !== undefined && <Chip label={`${mappingCount} ${countLabel}${mappingCount === 1 ? "" : "s"}`} size="small" sx={{ height: 18, fontSize: 9, color: "#6D28D9", bgcolor: "rgba(109,40,217,0.1)" }} />}
        </Box>
        <ObjectIdBadge objectId={objectId} onCopy={onCopy} compact />
      </Box>
    </Box>
  );
};

const CandidateNodeLabel = ({ type, name, icon = "", objectId = "", businessId = "", mapping, onCopy, onActivate }) => {
  const isFamily = type === "family";
  const isGroup = type === "group";
  const Icon = isFamily ? GroupsOutlinedIcon : isGroup ? AccountTreeOutlinedIcon : BadgeOutlinedIcon;
  const color = isFamily ? "#7C3AED" : isGroup ? "#2563EB" : "#15803D";
  const background = isFamily ? "rgba(124,58,237,0.11)" : isGroup ? "rgba(37,99,235,0.1)" : "rgba(21,128,61,0.1)";
  const aliases = Array.isArray(mapping?.role?.aliases) ? mapping.role.aliases : mapping?.role?.aliases?.en;
  const allowedLevelIds = mapping?.role?.allowed_level_ids || [];
  return (
    <Box display="flex" alignItems="center" gap={0.9} py={0.4} minWidth={0} width="100%">
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: isFamily ? 27 : 24, height: isFamily ? 27 : 24, flexShrink: 0, color, bgcolor: background, borderRadius: 1.3, fontSize: 15 }}>
        {isFamily && icon ? icon : <Icon sx={{ fontSize: isFamily ? 16 : 14 }} />}
      </Box>
      <Box
        minWidth={0}
        flex={1}
        onClick={onActivate ? (event) => { event.preventDefault(); event.stopPropagation(); onActivate(); } : undefined}
        sx={{ cursor: onActivate ? "pointer" : "default" }}
      >
        <Box display="flex" alignItems="center" gap={0.6} minWidth={0} flexWrap="wrap">
          <Typography sx={{ fontSize: isFamily ? 12.5 : 12, fontWeight: type === "role" ? 550 : 650, lineHeight: 1.25 }} noWrap>{name}</Typography>
          {mapping?.level?.level_id && <Chip label={mapping.level.level_id} size="small" sx={{ height: 17, fontSize: 9 }} />}
          {type === "role" && allowedLevelIds.map((levelId, index) => <Chip key={`${levelId}-${index}`} label={levelId} size="small" sx={{ height: 17, fontSize: 9 }} />)}
        </Box>
        {type !== "role" && businessId && <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 9.5 }}>{type === "family" ? "Family" : "Group"} ID: {businessId}</Typography>}
        {type !== "role" && <ObjectIdBadge objectId={objectId} onCopy={onCopy} compact />}
        {type === "role" && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 9.5 }} noWrap>
              Candidate role ID: {mapping.role?.role_id || "—"}{aliases?.length ? ` · Aliases: ${aliases.slice(0, 3).join(", ")}` : ""}
            </Typography>
            <ObjectIdBadge objectId={mapping.role?._id} label="Role ObjectId" onCopy={onCopy} compact />
            <ObjectIdBadge objectId={mapping._id} label={mapping.isCanonicalLink ? "Link ID" : "Mapping ID"} onCopy={onCopy} compact />
          </>
        )}
      </Box>
    </Box>
  );
};

const StatusLabel = ({ message, type = "info", loading = false, actionLabel = "", onAction }) => (
  <Box display="flex" alignItems="center" gap={0.8} py={0.7} color={type === "error" ? "error.main" : "text.secondary"}>
    {loading ? <CircularProgress size={14} /> : type === "error" ? <ErrorOutlineIcon sx={{ fontSize: 16 }} /> : <InfoOutlinedIcon sx={{ fontSize: 16 }} />}
    <Typography variant="caption">{message}</Typography>
    {onAction && <Button size="small" onClick={(event) => { event.preventDefault(); event.stopPropagation(); onAction(); }} sx={{ minWidth: 0, px: 0.75, fontSize: 10 }}>{actionLabel}</Button>}
  </Box>
);

const getTreeItemSx = (level, selected) => {
  const style = levelStyles[level];
  return {
    "& > .MuiTreeItem-content": {
      position: "relative",
      minHeight: level === "sector" ? 46 : level === "occupation" ? 42 : 38,
      mb: 0.35,
      pr: 1.2,
      borderRadius: 2,
      borderLeft: level === "role" && selected ? `3px solid ${style.color}` : "3px solid transparent",
      bgcolor: selected ? (theme) => theme.palette.mode === "dark" ? style.selectedDark : style.selectedLight : "transparent",
      transition: "background-color 0.15s ease, border-color 0.15s ease",
      "&:hover": { bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(92,75,58,0.07)" },
      "&.Mui-selected, &.Mui-selected:hover": { bgcolor: (theme) => `${selected ? theme.palette.mode === "dark" ? style.selectedDark : style.selectedLight : theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(92,75,58,0.07)"} !important` },
      ...(level === "role" ? { "&::before": { content: '""', position: "absolute", left: -23, top: "50%", width: 6, height: 6, borderRadius: "50%", bgcolor: style.color, transform: "translateY(-50%)" } } : {}),
    },
  };
};

const LevelLegend = ({ level, label }) => {
  const style = levelStyles[level];
  const Icon = style.Icon;
  return <Box display="flex" alignItems="center" gap={0.5}><Icon sx={{ fontSize: 15, color: style.color }} /><Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography></Box>;
};

const HierarchyTreeView = ({
  sectors = [],
  revision = 0,
  mappingRevision = 0,
  selectedSectorId = "",
  selectedOccupationId = "",
  selectedJobRoleId = "",
  onReveal = () => {},
  onCopy = () => {},
}) => {
  const [expanded, setExpanded] = useState([]);
  const [searchExpanded, setSearchExpanded] = useState([]);
  const [occupationsBySector, setOccupationsBySector] = useState({});
  const [rolesByPath, setRolesByPath] = useState({});
  const [mappingsByPath, setMappingsByPath] = useState({});
  const [loadingNodes, setLoadingNodes] = useState({});
  const [nodeErrors, setNodeErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [searchRetry, setSearchRetry] = useState(0);
  const { results: searchResults, loading: searchLoading, error: searchError } = useHierarchyRoleSearch(searchQuery, searchRetry);
  const searchActive = canSearchTreeRoles(searchQuery);
  const groupedSearchResults = useMemo(() => groupHierarchySearchResults(searchResults), [searchResults]);

  useEffect(() => {
    setExpanded([]);
    setOccupationsBySector({});
    setRolesByPath({});
    setMappingsByPath({});
    setLoadingNodes({});
    setNodeErrors({});
  }, [revision]);

  useEffect(() => {
    setMappingsByPath({});
    setExpanded((previous) => previous.filter((nodeId) => !nodeId.startsWith("role:")));
    setSearchExpanded((previous) => previous.filter((nodeId) => !nodeId.startsWith("role:")));
    setLoadingNodes((previous) => Object.fromEntries(Object.entries(previous).filter(([nodeId]) => !nodeId.startsWith("role:"))));
    setNodeErrors((previous) => Object.fromEntries(Object.entries(previous).filter(([nodeId]) => !nodeId.startsWith("role:"))));
  }, [mappingRevision]);

  useEffect(() => {
    if (!searchActive) return;
    const nodeIds = [];
    groupedSearchResults.forEach((sector) => {
      nodeIds.push(`sector:${sector._id}`);
      sector.occupations.forEach((occupation) => nodeIds.push(getOccupationNodeId(sector._id, occupation._id)));
    });
    setSearchExpanded(nodeIds);
  }, [groupedSearchResults, searchActive]);

  const setNodeLoading = (nodeId, value) => setLoadingNodes((previous) => ({ ...previous, [nodeId]: value }));

  const loadOccupations = async (sectorId, nodeId) => {
    if (Object.prototype.hasOwnProperty.call(occupationsBySector, sectorId) || loadingNodes[nodeId]) return;
    setNodeLoading(nodeId, true);
    setNodeErrors((previous) => ({ ...previous, [nodeId]: "" }));
    try {
      const response = await getRequestWithToken(`admin/occupations?sectorId=${sectorId}`);
      setOccupationsBySector((previous) => ({ ...previous, [sectorId]: response.data?.data || [] }));
    } catch (error) {
      if (error?.response?.data?.msg === "no occupation found") setOccupationsBySector((previous) => ({ ...previous, [sectorId]: [] }));
      else setNodeErrors((previous) => ({ ...previous, [nodeId]: "Could not load occupations." }));
    } finally {
      setNodeLoading(nodeId, false);
    }
  };

  const loadRoles = async (sectorId, occupationId, nodeId) => {
    const pathKey = `${sectorId}:${occupationId}`;
    if (Object.prototype.hasOwnProperty.call(rolesByPath, pathKey) || loadingNodes[nodeId]) return;
    setNodeLoading(nodeId, true);
    setNodeErrors((previous) => ({ ...previous, [nodeId]: "" }));
    try {
      const response = await getRequestWithToken(`admin/job-roles?occupation=${occupationId}`);
      setRolesByPath((previous) => ({ ...previous, [pathKey]: response.data?.data || [] }));
    } catch (error) {
      if (error?.response?.data?.msg === "no job role found") setRolesByPath((previous) => ({ ...previous, [pathKey]: [] }));
      else setNodeErrors((previous) => ({ ...previous, [nodeId]: "Could not load roles." }));
    } finally {
      setNodeLoading(nodeId, false);
    }
  };

  const loadMappings = async (sectorId, occupationId, roleId, nodeId, page = 1) => {
    const pathKey = `${sectorId}:${occupationId}:${roleId}`;
    const current = mappingsByPath[pathKey];
    if ((page === 1 && current) || loadingNodes[nodeId]) return;
    setNodeLoading(nodeId, true);
    setNodeErrors((previous) => ({ ...previous, [nodeId]: "" }));
    try {
      const response = await getRequestWithToken(buildCanonicalNcoLinkUrl(roleId));
      const incoming = (response.data?.data?.links || []).map(normalizeCanonicalLink);
      const pagination = response.data?.data?.pagination || {};
      setMappingsByPath((previous) => {
        const existing = page > 1 ? previous[pathKey]?.items || [] : [];
        const unique = [...existing, ...incoming].filter((mapping, index, all) => all.findIndex((item) => item._id === mapping._id) === index);
        return { ...previous, [pathKey]: { items: unique, total: pagination.total || 0, page: pagination.page || page, totalPages: pagination.totalPages || 0 } };
      });
    } catch (error) {
      setNodeErrors((previous) => ({ ...previous, [nodeId]: error?.response?.data?.msg || "Could not load the canonical Candidate Role link." }));
    } finally {
      setNodeLoading(nodeId, false);
    }
  };

  const handleToggle = (_, nodeIds) => {
    const currentExpanded = searchActive ? searchExpanded : expanded;
    const newlyExpanded = nodeIds.filter((nodeId) => !currentExpanded.includes(nodeId));
    if (searchActive) setSearchExpanded(nodeIds);
    else setExpanded(nodeIds);
    newlyExpanded.forEach((nodeId) => {
      const parts = nodeId.split(":");
      if (!searchActive && parts[0] === "sector" && parts.length === 2) loadOccupations(parts[1], nodeId);
      if (!searchActive && parts[0] === "occupation" && parts.length === 3) loadRoles(parts[1], parts[2], nodeId);
      if (parts[0] === "role" && parts.length === 4) loadMappings(parts[1], parts[2], parts[3], nodeId);
    });
  };

  const renderMappingChildren = (sectorId, occupationId, roleId, roleNodeId) => {
    const pathKey = `${sectorId}:${occupationId}:${roleId}`;
    const mappingState = mappingsByPath[pathKey];
    if (!mappingState && !loadingNodes[roleNodeId] && !nodeErrors[roleNodeId]) return <TreeItem nodeId={`${roleNodeId}:mapping-placeholder`} label={<StatusLabel message="Expand to load the canonical Candidate Role link" />} />;
    if (loadingNodes[roleNodeId] && !mappingState) return <TreeItem nodeId={`${roleNodeId}:mapping-loading`} label={<StatusLabel message="Loading canonical Candidate Role link..." loading />} />;
    if (nodeErrors[roleNodeId] && !mappingState) return <TreeItem nodeId={`${roleNodeId}:mapping-error`} label={<StatusLabel message={nodeErrors[roleNodeId]} type="error" actionLabel="Retry" onAction={() => loadMappings(sectorId, occupationId, roleId, roleNodeId)} />} />;
    if (mappingState?.items.length === 0) return <TreeItem nodeId={`${roleNodeId}:mapping-empty`} label={<StatusLabel message="No active canonical Candidate Role link" />} />;

    const families = groupCandidateMappings(mappingState?.items || []);
    return (
      <>
        {families.map((family) => (
          <TreeItem key={`${roleNodeId}:family:${family.key}`} nodeId={`${roleNodeId}:candidate-family:${family.key}`} label={<CandidateNodeLabel type="family" name={family.name} icon={family.icon} objectId={family.objectId} businessId={family.businessId} onCopy={onCopy} />}>
            {family.groups.map((group) => (
              <TreeItem key={`${roleNodeId}:group:${family.key}:${group.key}`} nodeId={`${roleNodeId}:candidate-group:${family.key}:${group.key}`} label={<CandidateNodeLabel type="group" name={group.name} objectId={group.objectId} businessId={group.businessId} onCopy={onCopy} />}>
                {group.mappings.map((mapping) => (
                  <TreeItem key={mapping._id} nodeId={`${roleNodeId}:candidate-role:${mapping._id}`} label={<CandidateNodeLabel type="role" name={mapping.role?.role_name || mapping.role?.role_id || "Unnamed candidate role"} mapping={mapping} onCopy={onCopy} />} />
                ))}
              </TreeItem>
            ))}
          </TreeItem>
        ))}
        {mappingState && mappingState.page < mappingState.totalPages && !nodeErrors[roleNodeId] && (
          <TreeItem nodeId={`${roleNodeId}:load-more:${mappingState.page + 1}`} label={<StatusLabel message={`${mappingState.items.length} of ${mappingState.total} canonical links loaded`} actionLabel="Load more" onAction={() => loadMappings(sectorId, occupationId, roleId, roleNodeId, mappingState.page + 1)} />} />
        )}
        {nodeErrors[roleNodeId] && mappingState && (
          <TreeItem nodeId={`${roleNodeId}:mapping-more-error`} label={<StatusLabel message={nodeErrors[roleNodeId]} type="error" actionLabel="Retry" onAction={() => loadMappings(sectorId, occupationId, roleId, roleNodeId, mappingState.page + 1)} />} />
        )}
        {loadingNodes[roleNodeId] && mappingState && <TreeItem nodeId={`${roleNodeId}:mapping-loading-more`} label={<StatusLabel message="Loading more canonical links..." loading />} />}
      </>
    );
  };

  const renderRole = (sector, occupation, role) => {
    const roleNodeId = getRoleNodeId(sector._id, occupation._id, role._id);
    const mappingState = mappingsByPath[`${sector._id}:${occupation._id}:${role._id}`];
    return (
      <TreeItem
        key={roleNodeId}
        nodeId={roleNodeId}
        label={<HierarchyNodeLabel level="role" name={role.jobRoleNameEn} objectId={role._id} ncoCode={role.ncoCode} mappingCount={mappingState?.total} countLabel="canonical link" onCopy={onCopy} onActivate={() => onReveal({ sector: { _id: sector._id, name: sector.sectorNameEn }, occupation: { _id: occupation._id, name: occupation.occupationNameEn }, role: { _id: role._id, name: role.jobRoleNameEn, ncoCode: role.ncoCode || "" } })} />}
        sx={getTreeItemSx("role", selectedSectorId === sector._id && selectedOccupationId === occupation._id && selectedJobRoleId === role._id)}
      >
        {renderMappingChildren(sector._id, occupation._id, role._id, roleNodeId)}
      </TreeItem>
    );
  };

  const treeSectors = searchActive ? groupedSearchResults : sectors;
  const selectedNodeId = getSelectedNodeId(selectedSectorId, selectedOccupationId, selectedJobRoleId);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.25 }, minHeight: 420, borderRadius: 2.5, bgcolor: (theme) => theme.palette.mode === "dark" ? "#1b1b1b" : "#f1ede8", borderColor: (theme) => theme.palette.mode === "dark" ? "#303030" : "#d4cdc4", boxShadow: (theme) => theme.palette.mode === "dark" ? "inset 0 1px 0 rgba(255,255,255,0.03)" : "inset 0 1px 0 rgba(255,255,255,0.7), 0 8px 22px rgba(50,38,26,0.07)" }}>
      <Box display="flex" alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" gap={1.5} mb={1.5} flexDirection={{ xs: "column", md: "row" }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={750}>Sector → Occupation → Role hierarchy</Typography>
          <Typography variant="caption" color="text.secondary">Search roles here or expand an NCO Role to inspect its canonical Candidate Role link.</Typography>
        </Box>
        <Box display="flex" gap={1.5}>
          <LevelLegend level="sector" label="Sector" /><LevelLegend level="occupation" label="Occupation" /><LevelLegend level="role" label="NCO Role" />
        </Box>
      </Box>
      <TextField
        size="small"
        fullWidth
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        placeholder="Search tree by role name, NCO code, or role ObjectId..."
        helperText={searchQuery.trim().length === 1 ? "Type at least 2 characters" : searchActive && !searchLoading && !searchError ? `${searchResults.length} matching path${searchResults.length === 1 ? "" : "s"}` : "Search filters the tree and keeps you in Tree View."}
        error={Boolean(searchError)}
        sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { bgcolor: (theme) => theme.palette.mode === "dark" ? "#242424" : "#faf8f5", borderRadius: 2, "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(255,140,0,0.12)" }, "&.Mui-focused fieldset": { borderColor: "#FF8C00", borderWidth: 2 } } }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
          endAdornment: <InputAdornment position="end">{searchLoading && <CircularProgress size={17} />}{searchError && !searchLoading && <IconButton size="small" aria-label="Retry tree role search" onClick={() => setSearchRetry((value) => value + 1)}><ReplayIcon fontSize="small" /></IconButton>}{searchQuery && <IconButton size="small" aria-label="Clear tree role search" onClick={() => setSearchQuery("")}><CloseIcon fontSize="small" /></IconButton>}</InputAdornment>,
        }}
      />
      {searchActive && searchError ? (
        <StatusLabel message={searchError} type="error" actionLabel="Retry" onAction={() => setSearchRetry((value) => value + 1)} />
      ) : searchActive && searchLoading && searchResults.length === 0 ? (
        <StatusLabel message="Searching hierarchy paths..." loading />
      ) : searchActive && !searchLoading && groupedSearchResults.length === 0 ? (
        <StatusLabel message="No matching roles found" />
      ) : (
        <TreeView
          expanded={searchActive ? searchExpanded : expanded}
          selected={selectedNodeId}
          onNodeToggle={handleToggle}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          sx={{ flexGrow: 1, overflowY: "auto", maxHeight: "65vh", pr: 0.75, "& .MuiTreeItem-iconContainer": { display: "flex !important", width: "22px !important", color: "text.secondary" }, "& .MuiTreeItem-group": { ml: 2.3, pl: 2.3, borderLeft: "1px dashed", borderColor: (theme) => theme.palette.mode === "dark" ? "#45413d" : "#c9bfb4" }, "&::-webkit-scrollbar": { width: 6 }, "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(110,95,80,0.35)", borderRadius: 5 } }}
        >
          {treeSectors.map((sector) => {
            const sectorNodeId = `sector:${sector._id}`;
            const occupations = searchActive ? sector.occupations : occupationsBySector[sector._id];
            return (
              <TreeItem key={sectorNodeId} nodeId={sectorNodeId} label={<HierarchyNodeLabel level="sector" name={sector.sectorNameEn} objectId={sector._id} onCopy={onCopy} />} sx={getTreeItemSx("sector", selectedSectorId === sector._id)}>
                {!searchActive && occupations === undefined && !loadingNodes[sectorNodeId] && !nodeErrors[sectorNodeId] && <TreeItem nodeId={`${sectorNodeId}:placeholder`} label={<StatusLabel message="Expand to load occupations" />} />}
                {!searchActive && loadingNodes[sectorNodeId] && <TreeItem nodeId={`${sectorNodeId}:loading`} label={<StatusLabel message="Loading occupations..." loading />} />}
                {!searchActive && nodeErrors[sectorNodeId] && <TreeItem nodeId={`${sectorNodeId}:error`} label={<StatusLabel message={nodeErrors[sectorNodeId]} type="error" actionLabel="Retry" onAction={() => loadOccupations(sector._id, sectorNodeId)} />} />}
                {occupations?.length === 0 && <TreeItem nodeId={`${sectorNodeId}:empty`} label={<StatusLabel message="No occupations" />} />}
                {(occupations || []).map((occupation) => {
                  const occupationNodeId = getOccupationNodeId(sector._id, occupation._id);
                  const roles = searchActive ? occupation.roles : rolesByPath[`${sector._id}:${occupation._id}`];
                  return (
                    <TreeItem key={occupationNodeId} nodeId={occupationNodeId} label={<HierarchyNodeLabel level="occupation" name={occupation.occupationNameEn} objectId={occupation._id} onCopy={onCopy} />} sx={getTreeItemSx("occupation", selectedSectorId === sector._id && selectedOccupationId === occupation._id)}>
                      {!searchActive && roles === undefined && !loadingNodes[occupationNodeId] && !nodeErrors[occupationNodeId] && <TreeItem nodeId={`${occupationNodeId}:placeholder`} label={<StatusLabel message="Expand to load roles" />} />}
                      {!searchActive && loadingNodes[occupationNodeId] && <TreeItem nodeId={`${occupationNodeId}:loading`} label={<StatusLabel message="Loading roles..." loading />} />}
                      {!searchActive && nodeErrors[occupationNodeId] && <TreeItem nodeId={`${occupationNodeId}:error`} label={<StatusLabel message={nodeErrors[occupationNodeId]} type="error" actionLabel="Retry" onAction={() => loadRoles(sector._id, occupation._id, occupationNodeId)} />} />}
                      {roles?.length === 0 && <TreeItem nodeId={`${occupationNodeId}:empty`} label={<StatusLabel message="No roles" />} />}
                      {(roles || []).map((role) => renderRole(sector, occupation, role))}
                    </TreeItem>
                  );
                })}
              </TreeItem>
            );
          })}
        </TreeView>
      )}
    </Paper>
  );
};

export default HierarchyTreeView;
